// attendanceThunks.js - UPDATED for simplified backend structure
import { createAsyncThunk } from '@reduxjs/toolkit';
import attendanceApi from '../../api/attendanceApi';

// ==================== TODAY'S SUMMARY ====================
/**
 * Fetch today's attendance summary for all employees
 * Manager/HR only
 */
export const fetchTodaySummary = createAsyncThunk(
  'attendance/fetchTodaySummary',
  async (_, { rejectWithValue }) => {
    try {
      const response = await attendanceApi.getTodaySummary();
      
      // Transform backend response to match state structure
      const { summary, records } = response.data;
      
      return {
        total: summary.total_records || 0,
        present: summary.present || 0,
        absent: summary.absent || 0,
        late: summary.late || 0,
        halfDay: summary.half_day || 0,
        onLeave: summary.on_leave || 0,
        medicalLeave: summary.medical_leave || 0,
        workFromHome: summary.work_from_home || 0,
        totalWorkHours: summary.total_work_hours || 0,
        avgWorkHours: summary.avg_work_hours || 0,
        totalEmployees: summary.total_employees || 0,
        employeesWithRecords: summary.employees_with_records || 0,
        employeesWithoutRecords: summary.employees_without_records || 0,
        date: summary.date,
        records: records || [],
      };
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error || 'Failed to fetch today\'s summary'
      );
    }
  }
);

// ==================== MY ATTENDANCE ====================
/**
 * Fetch current user's attendance records
 * Any authenticated employee
 */
export const fetchMyAttendance = createAsyncThunk(
  'attendance/fetchMyAttendance',
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await attendanceApi.getMyAttendance(params);
      
      const { employee, summary, records, count } = response.data;
      
      return {
        employeeId: employee.id,
        employeeName: employee.name,
        employeeCode: employee.employee_code,
        department: employee.department,
        role: employee.role,
        records: records || [],
        summary: {
          present: summary.present || 0,
          absent: summary.absent || 0,
          late: summary.late || 0,
          halfDay: summary.half_day || 0,
          onLeave: summary.on_leave || 0,
          medicalLeave: summary.medical_leave || 0,
          totalRecords: summary.total_records || 0,
          totalWorkHours: summary.total_work_hours || 0,
          avgWorkHours: summary.avg_work_hours || 0,
        },
        count: count || 0,
      };
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error || 'Failed to fetch your attendance'
      );
    }
  }
);

// ==================== EMPLOYEE SUMMARY ====================
/**
 * Fetch specific employee's attendance summary
 * Manager/HR only - REQUIRES employee_id
 */
export const fetchEmployeeSummary = createAsyncThunk(
  'attendance/fetchEmployeeSummary',
  async ({ employeeId, ...params }, { rejectWithValue }) => {
    try {
      if (!employeeId) {
        return rejectWithValue('Employee ID is required');
      }

      const response = await attendanceApi.getEmployeeSummary(employeeId, params);
      
      const { employee, summary, records, count, total_employees } = response.data;
      
      return {
        employeeId: employee.id,
        employeeName: employee.name,
        employeeCode: employee.employee_code,
        department: employee.department,
        role: employee.role,
        records: records || [],
        summary: {
          present: summary.present || 0,
          absent: summary.absent || 0,
          late: summary.late || 0,
          halfDay: summary.half_day || 0,
          onLeave: summary.on_leave || 0,
          medicalLeave: summary.medical_leave || 0,
          totalRecords: summary.total_records || 0,
          totalWorkHours: summary.total_work_hours || 0,
          avgWorkHours: summary.avg_work_hours || 0,
        },
        count: count || 0,
        totalEmployees: total_employees || 0,
      };
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error || 'Failed to fetch employee summary'
      );
    }
  }
);

// ==================== LIVE ATTENDANCE ====================
/**
 * Fetch employees currently checked in
 * Manager/HR only
 */
export const fetchLiveAttendance = createAsyncThunk(
  'attendance/fetchLiveAttendance',
  async (_, { rejectWithValue }) => {
    try {
      const response = await attendanceApi.getLiveAttendance();
      return response.data.records || [];
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error || 'Failed to fetch live attendance'
      );
    }
  }
);

// ==================== LEGACY - For backward compatibility ====================
/**
 * @deprecated Use fetchEmployeeSummary instead
 */
export const fetchEmployeeAttendance = fetchEmployeeSummary;

/**
 * @deprecated Use fetchTodaySummary instead
 */
export const fetchAttendanceRecords = fetchTodaySummary;

// ==================== UPDATE ATTENDANCE ====================
/**
 * Update attendance status
 */
export const updateAttendanceStatus = createAsyncThunk(
  'attendance/updateStatus',
  async ({ recordId, status, oldStatus }, { rejectWithValue }) => {
    try {
      const response = await attendanceApi.updateAttendanceStatus(recordId, { status });
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error || 'Failed to update attendance status'
      );
    }
  }
);

// ==================== CREATE ATTENDANCE ====================
/**
 * Create manual attendance entry
 */
export const createAttendance = createAsyncThunk(
  'attendance/create',
  async (attendanceData, { rejectWithValue }) => {
    try {
      const response = await attendanceApi.createAttendance(attendanceData);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error || 'Failed to create attendance'
      );
    }
  }
);

// ==================== DELETE ATTENDANCE ====================
/**
 * Delete attendance record
 */
export const deleteAttendance = createAsyncThunk(
  'attendance/delete',
  async (attendanceId, { rejectWithValue }) => {
    try {
      await attendanceApi.deleteAttendance(attendanceId);
      return attendanceId;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error || 'Failed to delete attendance'
      );
    }
  }
);

// ==================== BULK UPDATE ====================
/**
 * Bulk update attendance records
 */
export const bulkUpdateAttendance = createAsyncThunk(
  'attendance/bulkUpdate',
  async (records, { rejectWithValue }) => {
    try {
      const response = await attendanceApi.bulkUpdateAttendance(records);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error || 'Failed to bulk update attendance'
      );
    }
  }
);

// ==================== EXPORT ====================
/**
 * Export attendance to CSV
 */
export const exportAttendance = createAsyncThunk(
  'attendance/export',
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await attendanceApi.exportAttendanceCSV(params);
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `attendance_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      return { success: true };
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error || 'Failed to export attendance'
      );
    }
  }
);